import { useGetNotificationByAcl } from 'api-hooks/notification/query';
import { TourSaleLiteModel, useGetTourSales } from 'api-hooks/tour/tour-sale';
import { CheckCircleFillSVG } from 'common/assets';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import stylesColors from 'common/styles/styles.colors';
import { string2money } from 'common/utils/string';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { IColumn } from 'components/elements/table-component/table-component';
import { Container, TopContainer } from 'components/styles';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import useStatusFilter from 'hooks/use-status-filter';
import { commonSaleColumnTable } from 'legacy-modules/common/column';
import {
  BillingAddressTagname,
  CustomerListTagname,
} from 'legacy-modules/sales/component/list-tagname';
import SaleListTabs from 'legacy-modules/sales/component/sale-list-tabs';
import SalePillStatus from 'legacy-modules/sales/component/status';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import ToursaleTable from './components/table';

export default function TourSalesList() {
  const { t } = useTranslation();
  const formatDate = useDate();
  const { queryStatus, queryPage, queryTab } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const [activeKey, setActiveKey] = React.useState<React.Key>(queryTab);
  const [status, setStatus] = useStatusFilter<string | undefined>(
    queryStatus,
    setPage,
  );

  const _columns = React.useMemo<IColumn<TourSaleLiteModel>[]>(
    () => [
      {
        header: t('common:created_at'),
        accessorKey: 'createdAt',
        minSize: 150,
        sortName: 'created_at',
        cell: ({ getValue }) => {
          return <>{formatDate(getValue() as Date, 'dd MMM yyyy')}</>;
        },
      },
      {
        header: t('common:transaction_at'),
        accessorKey: 'transactionAt',
        minSize: 150,
        sortName: 'transaction_at',
        cell: ({ getValue }) => {
          return <>{formatDate(getValue() as Date, 'dd MMM yyyy')}</>;
        },
      },
      {
        header: t('common:invoice_status'),
        accessorKey: 'status',
        minSize: 100,
        sortName: 'status',
        cell: ({ getValue }) => <SalePillStatus value={getValue() as string} />,
      },
      {
        header: t('common:payment_status'),
        accessorKey: 'paymentStatus',
        minSize: 100,
        sortName: 'payment_status',
        cell: ({ getValue }) => <SalePillStatus value={getValue() as string} />,
      },
      {
        header: t('common:complete'),
        accessorKey: 'isCompleted',
        sortName: 'is_completed',
        minSize: 150,
        cell: ({ row }) => {
          const {
            original: { isCompleted },
          } = row;
          if (!isCompleted) {
            return null;
          }
          return <CheckCircleFillSVG color={stylesColors.primaryDefault} />;
        },
      },
      {
        header: t('common:invoice_number'),
        accessorKey: 'invoiceNumber',
        minSize: 180,
        sortName: 'invoice_number',
      },

      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 150,
        sortName: 'description',
      },
      {
        header: t('common:total_sales'),
        accessorKey: 'total',
        sortName: 'total',
        minSize: 150,
        cell: ({ row }) => {
          const { original } = row;
          return <>{string2money(original.total || 0)}</>;
        },
      },
      {
        header: t('common:customer'),
        accessorKey: 'customer',
        minSize: 100,
        cell: ({ row }) => {
          const customer = row.original.customer;
          return <CustomerListTagname customer={customer} />;
        },
      },
      {
        header: t('common:guest'),
        accessorKey: 'guest',
        minSize: 100,
        cell: ({ row }) => {
          const { original } = row;
          return <>{`${original.guests.join(', ')}`}</>;
        },
      },
      ...commonSaleColumnTable(t),
      {
        header: t('common:billing_address'),
        accessorKey: 'billingAddress',
        minSize: 100,
        cell: ({ row }) => {
          const billingAddress = row.original.billingAddress;
          return <BillingAddressTagname billingAddress={billingAddress} />;
        },
      },
      {
        header: t('common:created_by'),
        accessorKey: 'createdBy.name',
        minSize: 150,
        sortName: 'created_by',
        cell: ({ getValue }) => getValue(),
      },
    ],
    [formatDate, t],
  );
  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetTourSales,
    {
      params: {
        page,
        filter: activeKey === '6' ? { payment_status: status } : { status },
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  const { data: dataNotification, refetch: refetchNotification } =
    useGetNotificationByAcl({
      acl: AuthorizationRules.TourSalesPage,
    });

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);
  const permission = useGetCrudPermission('tour-sales');
  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:tour_sale')}
          showAdd={permission.create}
          onRefresh={() => {
            refetch();
            refetchNotification();
          }}
          linkAdd={NavigationRoutes.TourSalesCreate}
        />
        <SaleListTabs
          {...{
            activeKey,
            setActiveKey: (value) => {
              setActiveKey(value);
              refetchNotification();
              setFilters();
            },
            setStatus,
            badge: dataNotification?.data,
          }}
        />

        <TopContainer>
          <QueryFilter filters={filters} />
        </TopContainer>
        <ToursaleTable
          tourSales={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
